import { useState } from 'react';
import { LocationDescriptor } from 'history';
import { IntlShape, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import {
  IconCircleMinus,
  IconCustomFolderMove,
  IconDotsVertical,
  IconLibrary,
  IconLinkOff,
  IconLogs,
  IconSTurnRight,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  SvgIcon,
} from '@rossum/ui/material';
import { useIsFeatureEnabled } from '../../feature-enablement/featureEnablement';

export type RuleContextMenuItemsConfig = {
  intl: IntlShape;
  ruleManagerEnabled?: boolean;
  duplicateDisabled?: boolean;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onMoveToAnotherQueue?: () => void;
  onRemove?: () => void;
  onUnlink?: () => void;
  ruleExecutionLogsUrl?: LocationDescriptor;
  ruleManagerPath?: LocationDescriptor;
};

export const getRuleContextMenuItems = ({
  intl,
  ruleManagerEnabled,
  duplicateDisabled,
  onDuplicate,
  onDelete,
  onMoveToAnotherQueue,
  onRemove,
  onUnlink,
  ruleExecutionLogsUrl,
  ruleManagerPath,
}: RuleContextMenuItemsConfig) => {
  return [
    ...(onDuplicate
      ? [
          {
            key: 'duplicate',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.list.itemContextMenu.item.duplicate',
            }),
            Icon: IconLibrary,
            disabled: duplicateDisabled,
            onClick: onDuplicate,
            'data-cy': 'rule-context-menu-item-duplicate',
          },
        ]
      : []),
    ...(onMoveToAnotherQueue
      ? [
          {
            key: 'moveToAnotherQueue',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.list.itemContextMenu.item.moveToAnotherQueue',
            }),
            Icon: IconCustomFolderMove,
            onClick: onMoveToAnotherQueue,
            'data-cy': 'rule-context-menu-item-move-to-another-queue',
          },
        ]
      : []),
    ...(onUnlink
      ? [
          {
            key: 'unlink',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.list.itemContextMenu.item.unlink',
            }),
            Icon: IconLinkOff,
            onClick: onUnlink,
            'data-cy': 'rule-context-menu-item-unlink',
          },
        ]
      : []),
    ...(ruleExecutionLogsUrl
      ? [
          {
            key: 'executionLogs',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.list.itemContextMenu.item.debug',
            }),
            Icon: IconLogs,
            to: ruleExecutionLogsUrl,
            'data-cy': 'rule-context-menu-item-debug',
          },
        ]
      : []),
    ...(ruleManagerPath && ruleManagerEnabled
      ? [
          {
            key: 'openInRuleManager',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.list.itemContextMenu.item.open',
            }),
            Icon: IconSTurnRight,
            to: ruleManagerPath,
            'data-cy': 'rule-context-menu-item-open',
          },
        ]
      : []),
    ...(onDelete
      ? [
          {
            key: 'delete',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.list.itemContextMenu.item.delete',
            }),
            Icon: IconTrash,
            onClick: onDelete,
            'data-cy': 'rule-context-menu-item-delete',
          },
        ]
      : []),
    ...(onRemove
      ? [
          {
            key: 'remove',
            label: intl.formatMessage({
              id: 'features.queueSettings.rules.list.itemContextMenu.item.remove',
            }),
            Icon: IconCircleMinus,
            onClick: onRemove,
            'data-cy': 'rule-context-menu-item-remove',
          },
        ]
      : []),
  ];
};

type RuleContextMenuProps = Omit<
  RuleContextMenuItemsConfig,
  'intl' | 'ruleManagerEnabled'
> & {
  disabled: boolean;
};

export const RuleContextMenu = ({
  disabled,
  ...itemsConfig
}: RuleContextMenuProps) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const ruleManagerEnabled = useIsFeatureEnabled('rule-manager');

  const items = getRuleContextMenuItems({
    intl,
    ruleManagerEnabled,
    ...itemsConfig,
  });

  return (
    <>
      <IconButton
        disabled={disabled}
        data-cy="rule-context-menu-button"
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <SvgIcon fontSize="large">
          <IconDotsVertical />
        </SvgIcon>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {items.map(item => {
          const {
            key,
            Icon,
            label,
            disabled: itemDisabled,
            onClick,
            to,
            'data-cy': dataCy,
          } = item;

          return (
            <MenuItem
              key={key}
              data-cy={dataCy}
              disabled={itemDisabled}
              onClick={() => {
                onClick?.();
                setAnchorEl(null);
              }}
              {...(to && {
                component: RouterLink,
                to,
              })}
            >
              <ListItemIcon>
                <SvgIcon fontSize="large">
                  <Icon />
                </SvgIcon>
              </ListItemIcon>
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
